import React, { useState, useEffect } from "react";
import {
  Box,
  Chip,
  Typography,
  Autocomplete,
  TextField,
  MenuItem,
  Popper,
  Grid,
} from "@mui/material";
import { CheckCircleRounded } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import useFetchAuthTag from "../../hooks/useFetchAuthTag";

interface AuthTag {
  id: number;
  name: string;
  description?: string;
}

interface AssignTagsProps {
  selectedTags: number[];
  onTagChange: (tagIds: number[]) => void;
  disabled?: boolean;
  loading?: boolean;
  error?: string;
  label?: string;
  placeholder?: string;
  viewMode?: boolean;
  assignedTags?: any[];
}

const AssignTags: React.FC<AssignTagsProps> = ({
  selectedTags,
  onTagChange,
  disabled = false,
  loading = false,
  error = "",
  label = "Assign Tags",
  placeholder = "Select...",
  viewMode = false,
  assignedTags = [],
}) => {
  const theme = useTheme();
  const [selectedIds, setSelectedIds] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [authTagData] = useFetchAuthTag({ setIsLoading });

  useEffect(() => {
    setSelectedIds(selectedTags);
  }, [selectedTags]);

  const handleChange = (_event: any, newValue: any) => {
    const newSelectedTagIds = newValue.map((tag: { id: any }) => tag.id);
    setSelectedIds(newSelectedTagIds);
    onTagChange(newSelectedTagIds);
  };

  const renderTagsPopper = (props: any) => (
    <Popper
      {...props}
      className="filter-autocomplete-zindex"
      anchorEl={document.getElementById("assign-tags-autocomplete")}
    />
  );

  // View mode rendering
  if (viewMode) {
    return (
      <Box className="text-box-card-white" sx={{ mt: 2 }}>
        <Box className="text-box-header">
          <h3>{label}</h3>
        </Box>
        <Grid container rowSpacing={1.5} columnSpacing={2.5}>
          <Grid item xs={12}>
            <label className="label-text">Tags</label>
            <Box sx={{ mt: 1 }}>
              {assignedTags && assignedTags.length > 0 ? (
                <div className="tags-tooltip-parent">
                  {assignedTags.map((tag: any, index: number) => (
                    <Box className="chip" key={tag.id}>
                      <Typography variant="body2" fontWeight="medium">
                        {authTagData.find(
                          (authTag) => authTag.id === tag.auth_tag_id
                        )?.name || ""}
                      </Typography>
                    </Box>
                  ))}
                </div>
              ) : (
                <Box sx={{ color: "text.secondary", fontStyle: "italic" }}>
                  No tags assigned
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <>
      <div className="form-group non-flex">
        <div className="label-text">{label}</div>
        <div className="form-input">
          <Autocomplete
            id="assign-tags-autocomplete"
            PopperComponent={renderTagsPopper}
            fullWidth={true}
            className="form-control-autocomplete"
            multiple
            options={authTagData || []}
            getOptionLabel={(option: any) => option?.name || ""}
            disableCloseOnSelect
            disabled={disabled}
            onChange={handleChange}
            renderInput={(params) => (
              <>
                <div className="autocomplete-chips-direction">
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={placeholder}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </div>
              </>
            )}
            renderTags={(_value, getTagProps) => {
              const selectedTagObjects = selectedIds
                .map((tagId: number) =>
                  authTagData.find((tag) => tag.id === tagId)
                )
                .filter(
                  (tag: AuthTag | undefined): tag is AuthTag =>
                    tag !== undefined
                );

              return (
                <div className="align-from-left">
                  {selectedTagObjects.map((option: any, index: number) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={index}
                      label={option?.name}
                      sx={{
                        backgroundColor: theme.palette.secondary.light,
                        color: theme.palette.secondary.contrastText,
                        "& .MuiChip-deleteIcon": {
                          color: theme.palette.secondary.contrastText,
                        },
                      }}
                    />
                  ))}
                </div>
              );
            }}
            renderOption={(props, option: any, { selected }) => (
              <MenuItem
                {...props}
                key={`${option?.id}-${option?.name}`}
                value={option?.name}
                sx={{ justifyContent: "space-between" }}
              >
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {option?.name}
                  </Typography>
                </Box>
                {selected ? <CheckCircleRounded color="info" /> : null}
              </MenuItem>
            )}
            value={selectedIds
              .map((tagId: number) =>
                authTagData.find((tag) => tag.id === tagId)
              )
              .filter(
                (tag: AuthTag | undefined): tag is AuthTag => tag !== undefined
              )}
            isOptionEqualToValue={(option: any, value: any) =>
              option.id === value.id
            }
          />
        </div>
      </div>
    </>
  );
};

export default AssignTags;
